import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addProperty } from "../../redux/slices/propertySlice";
import { fetchPropertyTypes } from "../../redux/slices/propertyTypeSlice";
import { fetchPropertyStatuses } from "../../redux/slices/propertyStatusSlice";
import { fetchLocations } from "../../redux/slices/locationSlice";
import { toast } from "react-toastify";

import api from "../../api/axios";
import { fetchAllUsers } from "../../api/fetchAllUsers";
import Footer from "../../components/Footer";
import { useSettings } from "../../context/SettingsContext";

export default function PropertyForm({ property, onClose, onSaved }) {
  const dispatch = useDispatch();
  const { globalSettings } = useSettings();

  // Form state
  const [form, setForm] = useState({});

  // Photo management state
  const [newPhotos, setNewPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);

  // Documents state
  const [newDocuments, setNewDocuments] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);

  // Owner & Manager dropdown users
  const [owners, setOwners] = useState([]);
  const [managers, setManagers] = useState([]);

  // Redux dropdowns
  const types = useSelector((state) => state.propertyTypes.items);
  const statuses = useSelector((state) => state.propertyStatuses.items);

  // Load dropdown data
  useEffect(() => {
    dispatch(fetchPropertyTypes());
    dispatch(fetchPropertyStatuses());
    dispatch(fetchLocations());

    const fetchUsers = async () => {
      try {
        const users = await fetchAllUsers();
        setOwners(users);
        setManagers(users);
      } catch (error) {
        console.error(error);
        toast.error("Failed to fetch users data!");
      }
    };

    fetchUsers();
  }, [dispatch]);

  // Populate form when property is loaded
  useEffect(() => {
    if (property) {
      setForm({
        name: property.name || "",
        type_id: property.type_id || property.type?.id || "",
        status_id: property.status_id || property.status?.id || "",
        location_id: property.location_id || property.location?.id || "",
        total_floors: property.total_floors || "",
        total_units: property.total_units || "",
        area_sq_ft: property.area_sq_ft || "",
        owner_id: property.owner_id || "",
        manager_id: property.manager_id || "",
        description: property.description || "",
        flat_number: property.location?.flat_number || "",
        house_number: property.location?.house_number || "",
        road_number: property.location?.road_number || "",
        block: property.location?.block || "",
        area: property.location?.area || "",
        city: property.location?.city || "Dhaka",
        state: property.location?.state || "",
        postal_code: property.location?.postal_code || "",
        country: property.location?.country || "",
        latitude: property.location?.latitude || "",
        longitude: property.location?.longitude || "",
        booking_fee_amount: property.booking_fee_amount || "",
        down_payment_amount: property.down_payment_amount || "",
      });

      // ✅ Photos
      if (Array.isArray(property.photos)) {
        setExistingPhotos(property.photos);
      } else {
        setExistingPhotos([]);
      }

      // ✅ Documents (FIX)
      if (Array.isArray(property.documents)) {
        setExistingDocuments(property.documents);
      } else {
        setExistingDocuments([]);
      }
    } else {
      setExistingPhotos([]);
      setNewPhotos([]);
      setPhotoPreviews([]);
      setExistingDocuments([]);
      setNewDocuments([]);
      setForm({
        booking_fee_amount: globalSettings?.defaultBookingFee || "",
        down_payment_amount: globalSettings?.defaultDownpayment || "",
      });
    }
  }, [property, globalSettings]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value, files, type } = e.target;
    if (name === "photos") {
      handlePhotoSelect(files);
    } else if (name === "documents") {
      handleDocumentSelect(files);
    } else if (type === "number") {
      setForm({ ...form, [name]: value ? parseFloat(value) : "" });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  // Handle photo selection
  const handlePhotoSelect = (files) => {
    if (!files || files.length === 0) return;

    const totalPhotos = existingPhotos.length + newPhotos.length + files.length;
    if (totalPhotos > 10) {
      toast.error(
        `Maximum 10 photos allowed. You have ${
          existingPhotos.length + newPhotos.length
        } photos and trying to add ${files.length} more.`
      );
      return;
    }

    const fileArray = Array.from(files);
    setNewPhotos([...newPhotos, ...fileArray]);

    // Create previews
    const newPreviews = [];
    fileArray.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push({ file, preview: e.target.result });
        if (newPreviews.length === fileArray.length) {
          setPhotoPreviews([...photoPreviews, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove new photo (before upload)
  const removeNewPhoto = (index) => {
    const updated = newPhotos.filter((_, i) => i !== index);
    setNewPhotos(updated);
    const updatedPreviews = photoPreviews.filter((_, i) => i !== index);
    setPhotoPreviews(updatedPreviews);
  };

  // Delete existing photo
  const deletePhoto = async (photoId) => {
    if (!window.confirm("Are you sure you want to delete this photo?")) return;

    try {
      await api.delete(`/api/properties/${property.id}/photos/${photoId}`);
      setExistingPhotos(existingPhotos.filter((p) => p.id !== photoId));
      toast.success("Photo deleted successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete photo");
    }
  };

  // Set primary photo
  const setPrimaryPhoto = async (photoId) => {
    try {
      await api.put(`/api/properties/${property.id}/photos/${photoId}/primary`);
      setExistingPhotos(
        existingPhotos.map((p) => ({
          ...p,
          is_primary: p.id === photoId,
        }))
      );
      toast.success("Primary photo updated");
    } catch (error) {
      console.error(error);
      toast.error("Failed to set primary photo");
    }
  };

  // Reorder photos
  const reorderPhotos = async (photoIds) => {
    try {
      await api.put(`/api/properties/${property.id}/photos/reorder`, {
        photo_ids: photoIds,
      });
      // Reorder existing photos array
      const reordered = photoIds
        .map((id) => existingPhotos.find((p) => p.id === id))
        .filter(Boolean);
      setExistingPhotos(reordered);
      toast.success("Photos reordered");
    } catch (error) {
      console.error(error);
      toast.error("Failed to reorder photos");
    }
  };

  // Move photo up/down
  const movePhoto = (index, direction) => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === existingPhotos.length - 1)
    ) {
      return;
    }

    const newOrder = [...existingPhotos];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    [newOrder[index], newOrder[targetIndex]] = [
      newOrder[targetIndex],
      newOrder[index],
    ];
    setExistingPhotos(newOrder);

    // Update display_order on backend
    const photoIds = newOrder.map((p) => p.id);
    reorderPhotos(photoIds);
  };

  const handleDocumentSelect = (files) => {
    if (!files || files.length === 0) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/png",
    ];

    const selected = Array.from(files);

    const invalid = selected.find((file) => !allowedTypes.includes(file.type));
    if (invalid) {
      toast.error("Only PDF, DOC, DOCX, JPG, PNG files are allowed");
      return;
    }

    setNewDocuments((prev) => [...prev, ...selected]);
  };

  const removeNewDocument = (index) => {
    setNewDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm("Delete this document?")) return;

    try {
      await api.delete(`/api/properties/${property.id}/documents/${docId}`);
      setExistingDocuments((prev) => prev.filter((d) => d.id !== docId));
      toast.success("Document deleted");
    } catch (err) {
      toast.error("Failed to delete document");
    }
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const bookingFee = Number(form.booking_fee_amount || 0);
    const downPaymentThreshold = Number(form.down_payment_amount || 0);
    if (!Number.isFinite(bookingFee) || !Number.isFinite(downPaymentThreshold) || bookingFee < 0 || downPaymentThreshold < 0) {
      toast.error("Booking fee and down payment must be valid non-negative amounts.");
      return;
    }
    if (bookingFee > downPaymentThreshold) {
      toast.error("Booking fee cannot exceed the total down-payment threshold.");
      return;
    }
    try {
      const formData = new FormData();

      // Add form fields
      Object.keys(form).forEach((key) => {
        if (form[key] !== null && form[key] !== undefined && form[key] !== "") {
          formData.append(key, form[key]);
        }
      });

      // Add new photos
      if (newPhotos.length > 0) {
        newPhotos.forEach((photo) => {
          formData.append("photos[]", photo);
        });
      }

      // Add documents
      if (newDocuments.length > 0) {
        newDocuments.forEach((doc) => {
          formData.append("documents[]", doc);
        });
      }

      if (property?.id) {
        formData.append("_method", "PUT");
        const res = await api.post(`/api/properties/${property.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        console.log("UPDATE RESPONSE:", res.data);

        // Upload new photos if any
        if (newPhotos.length > 0) {
          const photoFormData = new FormData();
          newPhotos.forEach((photo) => {
            photoFormData.append("photos[]", photo);
          });
          await api.post(
            `/api/properties/${property.id}/photos`,
            photoFormData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
        }

        toast.success("Property updated successfully!");
      } else {
        await dispatch(addProperty(formData));
        toast.success("Property added successfully!");
      }

      onSaved();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong!");
    }
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      role="dialog"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        role="document"
      >
        <div className="modal-content">
          <form onSubmit={handleSubmit} encType="multipart/form-data">
            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title">
                {property ? "Edit Property" : "Add New Property"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>

            {/* Body */}
            <div className="modal-body">
              <div className="row g-3">
                {/* Property Name */}
                <div className="col-md-6">
                  <label className="form-label">Property Name</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Enter property name"
                    required
                  />
                </div>

                {/* Description */}
                <div className="col-md-12">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    className="form-control"
                    value={form.description}
                    onChange={handleChange}
                    placeholder="Property Description"
                    rows={3}
                  ></textarea>
                </div>

                {/* Property Type */}
                <div className="col-md-6">
                  <label className="form-label">Property Type</label>
                  <select
                    name="type_id"
                    className="form-select"
                    value={form.type_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Type --</option>
                    {types &&
                      types.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Status */}
                <div className="col-md-6">
                  <label className="form-label">Status</label>
                  <select
                    name="status_id"
                    className="form-select"
                    value={form.status_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Status --</option>
                    {statuses &&
                      statuses.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.status_type.charAt(0).toUpperCase() +
                            s.status_type.slice(1).toLowerCase()}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Owner */}
                <div className="col-md-6">
                  <label className="form-label">Owner</label>
                  <select
                    name="owner_id"
                    className="form-select"
                    value={form.owner_id || ""}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Owner --</option>
                    {owners.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Manager */}
                <div className="col-md-6">
                  <label className="form-label">Manager</label>
                  <select
                    name="manager_id"
                    className="form-select"
                    value={form.manager_id || ""}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Manager --</option>
                    {managers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Location Fields */}
                <div className="col-md-6">
                  <label className="form-label">Flat Number</label>
                  <input
                    type="text"
                    name="flat_number"
                    className="form-control"
                    value={form.flat_number || ""}
                    onChange={handleChange}
                    placeholder="Enter flat number"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">House Number</label>
                  <input
                    type="text"
                    name="house_number"
                    className="form-control"
                    value={form.house_number || ""}
                    onChange={handleChange}
                    placeholder="Enter house number"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Road Number</label>
                  <input
                    type="text"
                    name="road_number"
                    className="form-control"
                    value={form.road_number || ""}
                    onChange={handleChange}
                    placeholder="Enter road number"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Block</label>
                  <input
                    type="text"
                    name="block"
                    className="form-control"
                    value={form.block || ""}
                    onChange={handleChange}
                    placeholder="Enter block"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Area</label>
                  <input
                    type="text"
                    name="area"
                    className="form-control"
                    value={form.area || ""}
                    onChange={handleChange}
                    placeholder="Enter area"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    name="city"
                    className="form-control"
                    value={form.city || ""}
                    onChange={handleChange}
                    placeholder="Enter city"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">State</label>
                  <input
                    type="text"
                    name="state"
                    className="form-control"
                    value={form.state || ""}
                    onChange={handleChange}
                    placeholder="Enter state"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Postal Code</label>
                  <input
                    type="text"
                    name="postal_code"
                    className="form-control"
                    value={form.postal_code || ""}
                    onChange={handleChange}
                    placeholder="Enter postal code"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Country</label>
                  <input
                    type="text"
                    name="country"
                    className="form-control"
                    value={form.country || ""}
                    onChange={handleChange}
                    placeholder="Enter country"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Latitude</label>
                  <input
                    type="number"
                    name="latitude"
                    className="form-control"
                    value={form.latitude || ""}
                    onChange={handleChange}
                    placeholder="Enter latitude"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Longitude</label>
                  <input
                    type="number"
                    name="longitude"
                    className="form-control"
                    value={form.longitude || ""}
                    onChange={handleChange}
                    placeholder="Enter longitude"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Total Floors</label>
                  <input
                    type="number"
                    name="total_floors"
                    className="form-control"
                    value={form.total_floors}
                    onChange={handleChange}
                    placeholder="Enter total floors"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Total Units</label>
                  <input
                    type="number"
                    name="total_units"
                    className="form-control"
                    value={form.total_units || ""}
                    onChange={handleChange}
                    placeholder="Enter total units"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Booking Fee Amount (৳)</label>
                  <input
                    type="number"
                    name="booking_fee_amount"
                    className="form-control"
                    value={form.booking_fee_amount}
                    onChange={handleChange}
                    placeholder="Enter booking fee"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Down payment total, including booking fee (৳)</label>
                  <input
                    type="number"
                    name="down_payment_amount"
                    className="form-control"
                    value={form.down_payment_amount}
                    onChange={handleChange}
                    placeholder="Enter downpayment"
                    min="0"
                    step="0.01"
                  />
                  <div className="form-text">The booking fee counts toward this total. Customers pay only the remaining balance before installments begin.</div>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Land Area (Katha)</label>
                  <input
                    type="number"
                    name="area_sq_ft"
                    className="form-control"
                    value={form.area_sq_ft}
                    onChange={handleChange}
                    placeholder="Enter area"
                  />
                </div>

                {/* Photos Section */}
                <div className="col-md-12">
                  <label className="form-label">
                    Property Photos (Max 10) -{" "}
                    {existingPhotos.length + newPhotos.length}/10
                  </label>
                  <input
                    type="file"
                    name="photos"
                    accept="image/*"
                    multiple
                    className="form-control"
                    onChange={handleChange}
                    disabled={existingPhotos.length + newPhotos.length >= 10}
                  />
                  <small className="text-muted">
                    {existingPhotos.length + newPhotos.length >= 10
                      ? "Maximum 10 photos reached"
                      : `You can add ${
                          10 - (existingPhotos.length + newPhotos.length)
                        } more photos`}
                  </small>

                  {/* Existing Photos Grid */}
                  {existingPhotos.length > 0 && (
                    <div className="mt-3">
                      <h6>Existing Photos</h6>
                      <div className="row g-2">
                        {existingPhotos.map((photo, index) => (
                          <div
                            key={photo.id}
                            className="col-md-3 position-relative"
                          >
                            <div className="card">
                              <img
                                src={`${api.defaults.baseURL}/${photo.file_path}`}
                                alt={`Photo ${index + 1}`}
                                className="card-img-top"
                                style={{ height: "150px", objectFit: "cover" }}
                              />
                              <div className="card-body p-2">
                                {photo.is_primary && (
                                  <span className="badge bg-primary mb-1">
                                    Primary
                                  </span>
                                )}
                                <div
                                  className="btn-group btn-group-sm w-100"
                                  role="group"
                                >
                                  {index > 0 && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary"
                                      onClick={() => movePhoto(index, "up")}
                                      title="Move up"
                                    >
                                      ↑
                                    </button>
                                  )}
                                  {index < existingPhotos.length - 1 && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary"
                                      onClick={() => movePhoto(index, "down")}
                                      title="Move down"
                                    >
                                      ↓
                                    </button>
                                  )}
                                  {!photo.is_primary && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-info"
                                      onClick={() => setPrimaryPhoto(photo.id)}
                                      title="Set as primary"
                                    >
                                      ⭐
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    onClick={() => deletePhoto(photo.id)}
                                    title="Delete"
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New Photos Preview */}
                  {newPhotos.length > 0 && (
                    <div className="mt-3">
                      <h6>New Photos (will be uploaded)</h6>
                      <div className="row g-2">
                        {newPhotos.map((photo, index) => {
                          const preview = photoPreviews.find(
                            (p) => p.file === photo
                          );
                          return (
                            <div
                              key={index}
                              className="col-md-3 position-relative"
                            >
                              <div className="card">
                                <img
                                  src={
                                    preview?.preview ||
                                    URL.createObjectURL(photo)
                                  }
                                  alt={`New photo ${index + 1}`}
                                  className="card-img-top"
                                  style={{
                                    height: "150px",
                                    objectFit: "cover",
                                  }}
                                />
                                <div className="card-body p-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger w-100"
                                    onClick={() => removeNewPhoto(index)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Documents Section */}
                <div className="col-md-12 mt-4">
                  <label className="form-label">
                    Property Documents (PDF, DOC, Images)
                  </label>

                  <input
                    type="file"
                    name="documents"
                    className="form-control"
                    multiple
                    accept=".pdf,.doc,.docx,image/*"
                    onChange={handleChange}
                  />

                  {/* Existing Documents */}
                  {existingDocuments.length > 0 && (
                    <div className="mt-3">
                      <h6>Existing Documents</h6>
                      <ul className="list-group">
                        {existingDocuments.map((doc) => (
                          <li
                            key={doc.id}
                            className="list-group-item d-flex justify-content-between align-items-center"
                          >
                            <a
                              href={`${api.defaults.baseURL}/${doc.file_path}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {doc.original_name || "View Document"}
                            </a>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => deleteDocument(doc.id)}
                            >
                              Delete
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* New Documents */}
                  {newDocuments.length > 0 && (
                    <div className="mt-3">
                      <h6>New Documents (will be uploaded)</h6>
                      <ul className="list-group">
                        {newDocuments.map((doc, index) => (
                          <li
                            key={index}
                            className="list-group-item d-flex justify-content-between align-items-center"
                          >
                            {doc.name}
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeNewDocument(index)}
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {property ? "Update Property" : "Save Property"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
